import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import moment from 'moment';
import DateStrip from './DateStrip';
import DayViewSection from './DayViewSection';
import { HTTPService } from '../../../services/httpService';
import TasksAPI from '../../../services/tasksService';
import dateService from '../../../services/dateService';
import TaskActionModal from '../../partial/TaskActionModal';
import TaskCreateModal from '../../partial/TaskCreateModal';
import { convertFromUTC } from '../../../util/convert_timezone';
import { isAllLocations, isMobileLocation } from '../../../util/locationUtils';
import TodayMapView from './TodayMapView';
import NewSaleDrawer from '../../common/NewSaleDrawer';
import ReactGA from 'react-ga4';
import './TodayView.css';
import { isTechnician } from '../../../util/permissions';

const API = new HTTPService();

const isUnclaimed = (item) => {
  const locId = item.location_id || item.locationId;
  return !locId || locId === 0 || locId === '0';
};

// Section definitions per sub-view
const SHOP_SECTIONS = [
  {
    key: 'notStarted',
    title: 'Not Started',
    icon: 'fa-sharp fa-regular fa-battery-empty',
    color: '#3B82F6',
    filter: (item) => item.currentState === 'received',
    dateBound: false,
    emptyMessage: 'No vehicles waiting'
  },
  {
    key: 'inProgress',
    title: 'In Progress',
    icon: 'fa-sharp fa-regular fa-gears',
    color: '#F59E0B',
    filter: (item) => item.currentState === 'started',
    dateBound: false,
    emptyMessage: 'Nothing in progress'
  },
  {
    key: 'finished',
    title: 'Finished',
    icon: 'fa-sharp fa-regular fa-circle-check',
    color: '#10B981',
    filter: (item) => item.currentState === 'finished',
    dateBound: false,
    emptyMessage: 'Nothing finished yet'
  }
];

const TASKS_SECTION = {
  key: 'myTasks',
  title: 'My Reminders',
  icon: 'fa-sharp fa-regular fa-bell',
  color: '#8B5CF6',
  dataSource: 'tasks',
  filter: () => true,
  dateBound: false,
  emptyMessage: 'No open reminders'
};

// Front Desk: 4 columns — Leads, Estimates, Tasks, Arrivals+Pickups (stacked)
const FRONTDESK_COLUMNS = [
  {
    key: 'newLeads',
    sections: [{
      key: 'newLeads',
      title: 'New Leads',
      icon: 'fa-sharp fa-regular fa-bolt',
      color: '#3B82F6',
      dataSource: 'referrals',
      filter: () => true,
      dateBound: false,
      emptyMessage: 'No new leads'
    }]
  },
  {
    key: 'followUp',
    sections: [{
      key: 'estimatesOut',
      title: 'Estimates Out',
      icon: 'fa-sharp fa-regular fa-bell',
      color: '#8B5CF6',
      dataSource: 'items',
      filter: (item) => item.currentState === 'new' && item.offerState !== 'scheduled',
      dateBound: false,
      emptyMessage: 'No estimates awaiting response'
    }]
  },
  {
    key: 'tasks',
    sections: [TASKS_SECTION]
  },
  {
    key: 'arrivalsPickups',
    sections: [
      {
        key: 'arriving',
        title: 'Vehicles Arriving Today',
        icon: 'fa-sharp fa-regular fa-truck-ramp-box',
        color: '#F59E0B',
        dataSource: 'items',
        filter: (item) => item.currentState === 'scheduled',
        dateBound: true,
        emptyMessage: 'No arrivals this day'
      },
      {
        key: 'pickup',
        title: 'Ready for Pickup',
        icon: 'fa-sharp fa-regular fa-car-side',
        color: '#10B981',
        dataSource: 'items',
        filter: (item) => item.currentState === 'finished' || item.currentState === 'paid',
        dateBound: false,
        emptyMessage: 'No vehicles ready for pickup'
      }
    ]
  }
];

class TodayView extends Component {
  state = {
    selectedDate: moment(),
    subView: this.getDefaultSubView(),
    items: [],
    referrals: [],
    tasks: [],
    loading: true,
    taskModalItem: null,
    showCreateTask: false,
    staffList: [],
    refreshKey: 0,   // bump to signal child views to refetch
    refreshing: false,
  };

  getDefaultSubView() {
    const { user, company } = this.props;
    const companyId = company?.Installer_id || company?.dealer_id || 'default';
    const stored = localStorage.getItem(`todayViewSubView_${companyId}`);
    if (stored === 'shop' || stored === 'frontdesk' || stored === 'map') return stored;
    // Technicians default to shop, everyone else to frontdesk
    return isTechnician(user) ? 'shop' : 'frontdesk';
  }

  async componentDidMount() {
    await this.fetchData();
    window.addEventListener('obdClosed', this.handleObdClosed);
  }

  componentWillUnmount() {
    window.removeEventListener('obdClosed', this.handleObdClosed);
  }

  handleObdClosed = () => {
    this.fetchData();
  };

  // Single refresh entry-point that updates Front Desk / Shop data AND
  // signals TodayMapView to re-fetch its per-crew routes (via refreshKey prop).
  handleRefreshAll = async () => {
    this.setState({ refreshing: true });
    this.setState(prev => ({ refreshKey: prev.refreshKey + 1 }));
    try {
      await this.fetchData();
    } finally {
      this.setState({ refreshing: false });
    }
  };

  fetchData = async () => {
    const { typeId, userType, user } = this.props;
    this.setState({ loading: true });

    try {
      const requestsUrl = userType === 'dealer'
        ? `/requestimates/getdealerrequests/?Id=${typeId}`
        : `/requestimates/getinstallerrequests/?Id=${typeId}`;

      const promises = [API.get(requestsUrl)];
      if (userType === 'installer') {
        promises.push(API.get(`/requestimates/getinstallerestimates/?Id=${typeId}`));
        // Fetch leads (referrals)
        const isTech = isTechnician(user);
        const referralUrl = isTech
          ? `/referrals/${user.User_id}`
          : `/referrals/specialist/${typeId}`;
        promises.push(API.get(referralUrl).catch(() => ({ data: [] })));
        // Fetch tasks — no status filter so we get open + pending
        const taskParams = {
          installerId: typeId,
          limit: 200,
          userId: user.User_id,
          userRole: user.role
        };
        if (this.props.selectedLocationId && this.props.selectedLocationId !== 'all') {
          taskParams.locationId = this.props.selectedLocationId;
        }
        promises.push(TasksAPI.getAll(taskParams).catch(() => []));
        // Fetch staff for task creation modal
        promises.push(API.get(`/users?company_id=${typeId}&user_type=installer`).catch(() => ({ data: [] })));
      }

      const results = await Promise.all(promises);
      const requests = results[0].data || [];
      const estimates = results[1]?.data || [];
      const allItems = requests.concat(estimates);

      // Normalize referrals to a consistent shape for display
      const rawReferrals = results[2]?.data || [];
      const referralList = Array.isArray(rawReferrals)
        ? rawReferrals
        : (rawReferrals?.rows || rawReferrals?.referrals || []);

      // Tasks — exclude completed and cancelled
      const rawTasks = results[3] || [];
      const taskList = (Array.isArray(rawTasks) ? rawTasks : [])
        .filter(t => t.status !== 'completed' && t.status !== 'cancelled');

      // Staff list for task creation
      const rawStaff = results[4]?.data;
      const staffList = Array.isArray(rawStaff) ? rawStaff : (rawStaff?.users || []);

      this.setState({ items: allItems, referrals: referralList, tasks: taskList, staffList, loading: false });
    } catch (err) {
      console.error('TodayView: Error fetching data', err);
      this.setState({ items: [], referrals: [], tasks: [], loading: false });
    }
  };

  handleDateSelect = (date) => {
    this.setState({ selectedDate: date });
  };

  handleSubViewToggle = (view) => {
    const { company } = this.props;
    const companyId = company?.Installer_id || company?.dealer_id || 'default';
    localStorage.setItem(`todayViewSubView_${companyId}`, view);
    this.setState({ subView: view });
    const label = view === 'shop' ? 'Shop' : view === 'map' ? 'Map' : 'Front Desk';
    ReactGA.event({ category: 'Today', action: `Switched to ${label}`, label: 'prod' });
  };

  handleTaskActionComplete = () => {
    this.setState({ taskModalItem: null });
    this.fetchData();
    ReactGA.event({ category: 'Today', action: 'Completed Reminder', label: 'prod' });
  };

  handleItemClick = (item) => {
    // Task items open action modal
    if (item._isTask) {
      this.setState({ taskModalItem: item });
      ReactGA.event({ category: 'Today', action: 'Opened Reminder', label: 'prod' });
      return;
    }
    // Referral items navigate to referrals page
    if (item._isReferral) {
      this.props.history.push('/referrals', { selectReferralId: item.referral_id, fromToday: true });
      ReactGA.event({ category: 'Today', action: 'Opened Referral', label: 'prod' });
      return;
    }
    const state = item.currentState;
    let route;
    if (state === 'new' || state === 'answered') {
      route = '/requestimates';
    } else if (state === 'scheduled' || state === 'received' || state === 'started') {
      route = '/jobs';
    } else {
      route = '/pickups';
    }
    this.props.history.push(route, { selectRequestId: item.requestId, fromToday: true });
    ReactGA.event({ category: 'Today', action: 'Opened Job', label: 'prod' });
  };

  getFilteredData() {
    const { items, referrals, tasks } = this.state;
    const { selectedLocationId } = this.props;

    // NB: no multiLoc gate on these data filters — a user PINNED to one location
    // of a multi-location installer has locations.length === 1 but still needs
    // filtering. selectedLocationId alone encodes the need.
    const allLocView = isAllLocations(selectedLocationId);

    // Filter requestimates by location
    let filteredItems = items;
    if (!allLocView) {
      filteredItems = items.filter(item =>
        isUnclaimed(item) || String(item.location_id || item.locationId) === String(selectedLocationId)
      );
    }

    // Filter referrals by location
    let filteredReferrals = referrals;
    if (!allLocView) {
      filteredReferrals = referrals.filter(r =>
        !r.location_id || String(r.location_id) === String(selectedLocationId)
      );
    }

    // Filter tasks by location
    let filteredTasks = tasks;
    if (!allLocView) {
      filteredTasks = tasks.filter(t =>
        !t.location_id || String(t.location_id) === String(selectedLocationId)
      );
    }

    // Normalize referrals for card display — mark with _isReferral flag
    const normalizedReferrals = filteredReferrals.map(r => ({
      ...r,
      _isReferral: true,
      requestId: `ref-${r.referral_id}`,
      serviceRequested: r.requested_service || 'Service inquiry',
      vehicleYear: r.Year,
      vehicleMake: r.Make,
      vehicleModel: r.Model,
      vehicleColor: null,
      dealerName: r.customer_name,
      currentState: r.status || 'lead',
      assignedTechnicianName: null
    }));

    // Normalize tasks for card display — only overdue or due today
    const today = moment();
    const normalizedTasks = filteredTasks
      .filter(t => {
        if (!t.due_date) return true; // no due date = always actionable
        const due = dateService.fromAPIDate(t.due_date);
        return due.isSameOrBefore(today, 'day');
      })
      .map(t => ({
        ...t,
        _isTask: true,
        requestId: `task-${t.task_id}`
      }));

    return { filteredItems, filteredReferrals: normalizedReferrals, filteredTasks: normalizedTasks };
  }

  getSectionItems(section, filteredItems, filteredReferrals, filteredTasks) {
    const { selectedDate } = this.state;
    let source;
    if (section.dataSource === 'referrals') source = filteredReferrals;
    else if (section.dataSource === 'tasks') source = filteredTasks;
    else source = filteredItems;
    let sectionItems = source.filter(section.filter);

    if (section.dateBound) {
      sectionItems = sectionItems.filter(item => {
        const start = item.scheduledStart
          ? convertFromUTC(item.scheduledStart)
          : null;
        return start && start.isSame(selectedDate, 'day');
      });
    }

    return sectionItems;
  }

  render() {
    const { selectedDate, subView, loading } = this.state;
    const { userType } = this.props;
    const { locations } = this.props;

    // Scope the toggle/section visibility to the *selected* location, not the
    // installer's overall location mix. "All Locations" falls back to the
    // installer-wide mix (combined view needs every applicable sub-view).
    const isAllSelected = isAllLocations(this.props.selectedLocationId);
    const selectedLocation = isAllSelected
      ? null
      : (locations || []).find(l => String(l.location_id) === String(this.props.selectedLocationId));

    const hasMobileLocation = isAllSelected
      ? (locations || []).some(isMobileLocation)
      : (selectedLocation ? isMobileLocation(selectedLocation) : false);
    const hasFixedLocation = isAllSelected
      ? (locations || []).some(l => !isMobileLocation(l))
      : (selectedLocation ? !isMobileLocation(selectedLocation) : false);

    // Effective subView — silently fall back when a sub-view has been gated
    // out (e.g. pure-mobile installer with stale 'shop' in localStorage).
    let effectiveSubView = subView;
    if (effectiveSubView === 'shop' && !hasFixedLocation) effectiveSubView = 'frontdesk';
    if (effectiveSubView === 'map' && !hasMobileLocation) effectiveSubView = 'frontdesk';

    // Resolve item → location type. Used by Front Desk sections so the
    // Arrivals / Pickups columns reflect fixed-shop activity only, while
    // mobile stops surface in their own "On Route Today" section.
    const isMobileItem = (item) => {
      const locId = item.location_id || item.locationId;
      if (!locId) return false;
      const loc = (locations || []).find(l => String(l.location_id) === String(locId));
      return loc ? isMobileLocation(loc) : false;
    };

    const frontdeskColumns = [
      FRONTDESK_COLUMNS[0], // New Leads
      FRONTDESK_COLUMNS[1], // Estimates Out
      FRONTDESK_COLUMNS[2], // Tasks
      {
        key: 'todaysActivity',
        sections: [
          hasFixedLocation && {
            key: 'arriving',
            title: 'Vehicles Arriving Today',
            icon: 'fa-sharp fa-regular fa-truck-ramp-box',
            color: '#F59E0B',
            dataSource: 'items',
            filter: (item) => item.currentState === 'scheduled' && !isMobileItem(item),
            dateBound: true,
            emptyMessage: 'No arrivals this day',
          },
          hasMobileLocation && {
            key: 'onRoute',
            title: 'On Route Today',
            icon: 'fa-sharp fa-regular fa-route',
            color: '#3B82F6',
            dataSource: 'items',
            filter: (item) => item.currentState === 'scheduled' && isMobileItem(item),
            dateBound: true,
            emptyMessage: 'No stops scheduled',
          },
          hasFixedLocation && {
            key: 'pickup',
            title: 'Ready for Pickup',
            icon: 'fa-sharp fa-regular fa-car-side',
            color: '#10B981',
            dataSource: 'items',
            filter: (item) => (item.currentState === 'finished' || item.currentState === 'paid') && !isMobileItem(item),
            dateBound: false,
            emptyMessage: 'No vehicles ready for pickup',
          },
        ].filter(Boolean),
      },
    ];

    // Shop view should NOT show mobile crew stops — they belong on the Map
    // and in Front Desk's "On Route Today" section. Wrap each SHOP_SECTIONS
    // filter to exclude mobile items.
    const shopColumns = [
      {
        key: 'arrivingNotStarted',
        sections: [
          {
            key: 'arriving',
            title: 'Vehicles Arriving Today',
            icon: 'fa-sharp fa-regular fa-truck-ramp-box',
            color: '#6366F1',
            dataSource: 'items',
            filter: (item) => item.currentState === 'scheduled' && !isMobileItem(item),
            dateBound: true,
            emptyMessage: 'No arrivals this day',
          },
          {
            ...SHOP_SECTIONS[0],
            filter: (item) => SHOP_SECTIONS[0].filter(item) && !isMobileItem(item),
          },
        ],
      },
      {
        key: 'inProgress',
        sections: [{
          ...SHOP_SECTIONS[1],
          filter: (item) => SHOP_SECTIONS[1].filter(item) && !isMobileItem(item),
        }],
      },
      {
        key: 'finished',
        sections: [{
          ...SHOP_SECTIONS[2],
          filter: (item) => SHOP_SECTIONS[2].filter(item) && !isMobileItem(item),
        }],
      },
      { key: 'tasks', sections: [TASKS_SECTION] },
    ];

    const columns = effectiveSubView === 'shop' ? shopColumns : frontdeskColumns;
    const { filteredItems, filteredReferrals, filteredTasks } = this.getFilteredData();

    return (
      <div className="today-view">
        <div className="today-header">
          <div className="today-title-row">
            <h4 className="today-title">
              <i className="fa-sharp fa-regular fa-sun" /> Today
              <button
                type="button"
                className="btn btn-sm btn-link p-0 ml-2 align-baseline"
                onClick={this.handleRefreshAll}
                disabled={this.state.refreshing}
                title="Refresh"
                aria-label="Refresh"
              >
                <i
                  className={`fa-sharp fa-regular fa-arrow-rotate-right ${this.state.refreshing ? 'fa-spin' : ''}`}
                  aria-hidden="true"
                />
              </button>
            </h4>
            <div className="today-header-actions">
              {userType === 'installer' && !isAllLocations(this.props.selectedLocationId) && (
                <Link to="/newestimate/" className="jv-btn jv-btn-primary jv-btn-sm today-new-estimate-btn">
                  <i className="fa-sharp fa-regular fa-plus" /> New Estimate
                </Link>
              )}
              {userType === 'installer' && (
                <button
                  className="jv-btn jv-btn-sm today-new-estimate-btn"
                  style={{ background: '#8B5CF6', borderColor: '#8B5CF6', color: '#fff' }}
                  onClick={() => this.setState({ showCreateTask: true })}
                >
                  <i className="fa-sharp fa-regular fa-plus" /> New Reminder
                </button>
              )}
              {userType === 'installer' && !isAllLocations(this.props.selectedLocationId) && (
                <button
                  className="jv-btn jv-btn-sm today-new-estimate-btn"
                  style={{ background: '#3FB2BE', borderColor: '#3FB2BE', color: '#fff' }}
                  onClick={() => this.setState({ showRegister: true })}
                >
                  <i className="fa-sharp fa-regular fa-cash-register" /> Register
                </button>
              )}
              <NewSaleDrawer
                show={!!this.state.showRegister}
                onHide={() => this.setState({ showRegister: false })}
                installerId={this.props.typeId}
                locationId={this.props.selectedLocationId}
                createdBy={this.props.user?.User_id}
                taxRate={0}
              />
            <div className="today-subview-toggle">
              <button
                className={`today-toggle-btn ${effectiveSubView === 'frontdesk' ? 'active' : ''}`}
                onClick={() => this.handleSubViewToggle('frontdesk')}
              >
                <i className="fa-sharp fa-regular fa-desktop" /> Front Desk
              </button>
              {hasFixedLocation && (
                <button
                  className={`today-toggle-btn ${effectiveSubView === 'shop' ? 'active' : ''}`}
                  onClick={() => this.handleSubViewToggle('shop')}
                >
                  <i className="fa-sharp fa-regular fa-warehouse" /> Shop
                </button>
              )}
              {hasMobileLocation && (
                <button
                  className={`today-toggle-btn ${effectiveSubView === 'map' ? 'active' : ''}`}
                  onClick={() => this.handleSubViewToggle('map')}
                >
                  <i className="fa-sharp fa-regular fa-map" /> Map
                </button>
              )}
            </div>
            </div>
          </div>
          <DateStrip selectedDate={selectedDate} onDateSelect={this.handleDateSelect} />
        </div>

        {loading ? (
          <div className="today-loading">
            <i className="fa-sharp fa-solid fa-spinner fa-spin" /> Loading...
          </div>
        ) : effectiveSubView === 'map' ? (
          <TodayMapView
            locations={locations}
            selectedDate={selectedDate}
            refreshKey={this.state.refreshKey}
          />
        ) : (
          <div className="today-columns">
            {columns.map((col) => (
              <div className="today-column" key={col.key}>
                {col.sections.map((section) => (
                  <DayViewSection
                    key={section.key}
                    title={section.title}
                    icon={section.icon}
                    color={section.color}
                    items={this.getSectionItems(section, filteredItems, filteredReferrals, filteredTasks)}
                    userType={userType}
                    onItemClick={this.handleItemClick}
                    emptyMessage={section.emptyMessage}
                    locations={locations}
                    selectedLocationId={this.props.selectedLocationId}
                    technicians={this.state.staffList}
                  />
                ))}
              </div>
            ))}
          </div>
        )}

        <TaskActionModal
          show={!!this.state.taskModalItem}
          onHide={() => this.setState({ taskModalItem: null })}
          task={this.state.taskModalItem}
          user={this.props.user}
          onActionComplete={this.handleTaskActionComplete}
        />

        <TaskCreateModal
          show={this.state.showCreateTask}
          onHide={() => this.setState({ showCreateTask: false })}
          onCreated={() => { this.fetchData(); window.dispatchEvent(new Event('refreshNavbarCounts')); ReactGA.event({ category: 'Today', action: 'Created Reminder', label: 'prod' }); }}
          installerId={this.props.typeId}
          userId={this.props.user.User_id}
          locationId={this.props.selectedLocationId}
          staffList={this.state.staffList}
        />
      </div>
    );
  }
}

export default TodayView;
